
import React from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Brain, Check, Globe, MoreVertical, RefreshCw, Volume2, Zap } from "lucide-react";

interface EnhancedChatHeaderProps {
  currentLanguage: string;
  onLanguageChange: (language: string) => void;
  onReset: () => void;
  continuousMode: boolean;
  onContinuousModeChange: (enabled: boolean) => void;
  autoVoiceResponse: boolean;
  onAutoVoiceResponseChange: (enabled: boolean) => void;
  voiceOnlyMode: boolean;
  onVoiceOnlyModeChange: (enabled: boolean) => void;
  onRealtimeClick?: () => void;
}

export const EnhancedChatHeader: React.FC<EnhancedChatHeaderProps> = ({
  currentLanguage,
  onLanguageChange,
  onReset,
  continuousMode,
  onContinuousModeChange,
  autoVoiceResponse,
  onAutoVoiceResponseChange,
  voiceOnlyMode,
  onVoiceOnlyModeChange,
  onRealtimeClick
}) => {
  const getLanguageLabel = (code: string) => {
    switch (code) {
      case 'fr': return '🇫🇷 Français';
      case 'ht': return '🇭🇹 Kreyòl';
      case 'en': return '🇺🇸 English';
      case 'es': return '🇪🇸 Español';
      case 'ar': return '🇸🇦 العربية';
      default: return '🇫🇷 Français';
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 px-4 py-3 bg-white border-b">
      <div className="flex items-center gap-2">
        <div className="bg-gradient-to-br from-indigo-500 to-blue-600 rounded-full p-1.5">
          <Brain className="h-4 w-4 text-white" />
        </div>
        <span className="font-semibold text-blue-700">Assistant médical</span>
      </div>

      <div className="flex items-center gap-2">
        {/* Bouton pour le mode temps réel */}
        {onRealtimeClick && (
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={onRealtimeClick}
          >
            <Volume2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Temps réel</span>
          </Button>
        )}

        {/* Menu de langue */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1 border-blue-200">
              <Globe className="h-3.5 w-3.5 text-blue-700" />
              <span className="hidden sm:inline text-blue-700">{currentLanguage}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {['fr', 'ht', 'en', 'es', 'ar'].map((lang) => (
              <DropdownMenuItem 
                key={lang} 
                onClick={() => onLanguageChange(lang)}
                className="flex items-center gap-2"
              >
                {currentLanguage === lang && <Check className="h-3.5 w-3.5 text-green-500" />}
                <span className={currentLanguage === lang ? 'font-medium' : ''}>{getLanguageLabel(lang)}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Reset button */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={onReset}
          className="text-blue-700"
          title="Réinitialiser la conversation"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>

        {/* Settings dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-blue-700">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1.5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Réponses vocales IA</span>
                </div>
                <Switch 
                  checked={autoVoiceResponse} 
                  onCheckedChange={onAutoVoiceResponseChange} 
                  className="data-[state=checked]:bg-blue-600" 
                />
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Mode vocal uniquement</span>
                </div>
                <Switch 
                  checked={voiceOnlyMode} 
                  onCheckedChange={onVoiceOnlyModeChange} 
                  className="data-[state=checked]:bg-blue-600" 
                  disabled={!autoVoiceResponse}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Mode continu</span>
                </div>
                <Switch 
                  checked={continuousMode} 
                  onCheckedChange={onContinuousModeChange} 
                  className="data-[state=checked]:bg-blue-600" 
                />
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
